import { ActionConfirmation, usePublishActionOutcome, type ActionOutcome } from "../ui/ActionConfirmation.js";
import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { ApiError } from "../api.js";
import {
  dayKey,
  formatDate,
  formatWeekdayDay,
  formatTime,
  isoDay,
} from "../format.js";
import { useAsync } from "../useAsync.js";
import {
  booking,
  idempotencyKey,
  type AvailabilitySlot,
  type BookingOffering,
  type MutationResult,
  type LessonCreditBlock,
  type StudentLessonSchedule,
  type StudentLessonScheduleRow,
} from "./api.js";
import {
  fourWeeksFrom,
  groupAvailabilitySlots,
  lessonIntervalsOverlap,
} from "./availabilityRange.js";
import {
  bookingAttemptAfterFailure,
  bookingFailureKind,
  uncertainBookingMessageFor,
} from "./attempt.js";
import {
  bookingPreferenceForOffering,
  confirmedLessonInSameTeachingWeek,
  deriveStudentBookingPreferences,
  latestConfirmedUpcomingLessonStart,
  nearestUsualAvailabilitySlots,
  slotMatchesUsualPreference,
  unambiguousUsualBookingPreference,
  type StudentBookingPreference,
} from "./bookingGuardrails.js";
import { bookableWithCredit, describeCredit, describePayment, reasonText, Reasons } from "./policy.js";
import {
  bookEachSlot,
  bulkBookingSummary,
  canAddSlot,
  selectionLimitMessage,
} from "./bulkBooking.js";
import {
  partitionBookingFailureRows,
  reconcileSelectedAvailability,
  type BookingFailureView,
} from "./bulkBookingUi.js";
import { recentAmeliaPurchaseMessage } from "./creditSync.js";
import { defaultBookingOffering } from "./offeringDefault.js";
import { RequestTimeEntry } from "../waitlist/RequestTimePicker.js";
import { waitlistApi } from "../waitlist/api.js";
import { AvailabilityCalendar, availabilityMonthWindow } from "./AvailabilityCalendar.js";
import "./creditBooking.css";

export function familyBookingOfferingKey(
  offering: Pick<BookingOffering, "provider_id" | "service_id">,
): string {
  return `${offering.provider_id}:${offering.service_id}`;
}

export function familyBookingDefaultOfferingKey(
  offerings: readonly BookingOffering[],
): string | null {
  const valid = offerings.filter(
    (offering) =>
      offering.book_with_credit.allowed || offering.request_onsite.allowed,
  );
  const withCredit = valid.filter(
    (offering) => offering.book_with_credit.allowed && offering.available_credits > 0,
  );
  const chosen = defaultBookingOffering(withCredit.length > 0 ? withCredit : valid);
  return chosen === null ? null : familyBookingOfferingKey(chosen);
}

const RECOMMENDATION_WEEKDAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;

function preferenceTiming(preference: StudentBookingPreference): string | null {
  if (preference.usualWeekday === null) return null;
  const weekday = RECOMMENDATION_WEEKDAYS[preference.usualWeekday];
  if (!weekday) return null;
  return preference.usualLocalTime
    ? `Usually ${weekday}s around ${preference.usualLocalTime}`
    : `Usually ${weekday}s`;
}

interface DuplicateWarning {
  slot: AvailabilitySlot;
  lesson: StudentLessonScheduleRow;
}

function duplicateWarningsForSelection(
  schedule: StudentLessonSchedule | null,
  selected: readonly AvailabilitySlot[],
): DuplicateWarning[] {
  const warnings: DuplicateWarning[] = [];
  const seenBookingIds = new Set<string>();
  for (const slot of selected) {
    const lesson = confirmedLessonInSameTeachingWeek(schedule, slot);
    if (!lesson || seenBookingIds.has(lesson.bookingId)) continue;
    seenBookingIds.add(lesson.bookingId);
    warnings.push({ slot, lesson });
  }
  return warnings;
}

interface ConfirmedBookingHold {
  studentId: string;
  providerId: number;
  serviceId: number;
  bookingId: number;
  appointmentId: number;
  /** Set for a pay-on-the-day request, which has no booking until the tutor confirms. */
  requestId?: string;
  starts_at: string;
  ends_at: string;
  presentInSchedule: boolean;
}

/**
 * Booking another lesson.
 *
 * Two ways to pay for it, and the difference is worth being blunt about on the
 * screen rather than in a tooltip: a credit-funded booking **is** a booking, and
 * an on-site request is a *request* that holds the time until the tutor says
 * yes. A family that thought it had booked a lesson and turns up to find nobody
 * expecting them has been failed by wording, not by the API.
 *
 * What can be booked comes from FastAPI's offerings — the union of the tutor and
 * lesson pairings the Hub can prove this student has been taught, and the ones
 * their lesson blocks entitle them to, with eligibility already decided for
 * each. The usual-time hint is presentation-only and cannot create an offering
 * or an available slot.
 */
export function BookLesson({
  studentId,
  offerings,
  onBooked,
  onWaitlistChanged = () => undefined,
  creditBlock,
  onClearCredit,
  creditUnconfirmed = false,
}: {
  studentId: string;
  offerings: BookingOffering[];
  onBooked: () => void;
  onWaitlistChanged?: () => void;
  creditBlock?: LessonCreditBlock;
  onClearCredit?: () => void;
  /** The balance shown is retained presentation data, not a confirmed count. */
  creditUnconfirmed?: boolean;
}) {
  const [selection, setSelection] = useState<{
    studentId: string;
    key: string | null;
  }>(() => ({
    studentId,
    key: null,
  }));
  const scheduleRead = useAsync(
    () => booking.lessonSchedule(studentId),
    [studentId],
  );
  const [choicesForStudent, setChoicesForStudent] = useState<string | null>(null);
  // Confirmed bookings outlive the keyed time picker: changing offering,
  // credit block or learner must not forget a successful write while reads lag.
  const [confirmedHolds, setConfirmedHolds] = useState<ConfirmedBookingHold[]>([]);

  // useAsync deliberately retains the last successful value while reloading so
  // guidance does not blank after a booking. Its student id still has to match:
  // when the family switcher changes student, the previous student's retained
  // value must never flash in the new student's booking view.
  const schedule =
    scheduleRead.data?.student.id === studentId ? scheduleRead.data : null;
  const scheduleFresh = !scheduleRead.loading && !scheduleRead.error && schedule !== null;
  useEffect(() => {
    if (!scheduleFresh || !schedule) return;
    const rows = [...schedule.upcoming, ...schedule.past];
    setConfirmedHolds(current => current.flatMap(hold => {
      // Only this learner's current schedule can reconcile their holds.
      if (hold.studentId !== studentId) return [hold];
      const closed = (status: string) => ["cancelled", "canceled", "rejected", "declined", "removed"].includes(status.toLowerCase());
      if (hold.requestId !== undefined) {
        // A pay-on-the-day request has no booking id until the tutor answers,
        // so match its lesson row by learner-visible identity: the start
        // instant and, when the row names them, the tutor and service.
        const matches = rows.filter(candidate => Date.parse(candidate.startsAt) === Date.parse(hold.starts_at)
          && (candidate.providerId == null || candidate.providerId === hold.providerId)
          && (candidate.serviceId == null || candidate.serviceId === hold.serviceId));
        if (matches.some(candidate => !closed(candidate.status))) return [{ ...hold, presentInSchedule: true }];
        // Once the request has been seen, its row going away or being declined
        // releases the time. A closed row seen before that could be an older
        // lesson at the same time, so it does not release a fresh request.
        return hold.presentInSchedule ? [] : [hold];
      }
      const row = rows.find(candidate => candidate.bookingId === String(hold.bookingId));
      if (row && closed(row.status)) return [];
      if (row) return [{ ...hold, presentInSchedule: true }];
      // An initially absent booking can still be waiting for the diary mirror.
      // One that never appears in the loaded schedule keeps its slot blocked
      // until the page remounts; that is deliberate, as freeing it early could
      // let a duplicate booking through.
      return hold.presentInSchedule ? [] : [hold];
    }));
  }, [schedule, scheduleFresh, studentId]);
  const preferences = useMemo(
    () => deriveStudentBookingPreferences(offerings, schedule),
    [offerings, schedule],
  );
  const explicitKey = selection.studentId === studentId ? selection.key : null;
  const explicitStillExists =
    explicitKey !== null &&
    offerings.some(
      (offering) => familyBookingOfferingKey(offering) === explicitKey,
    );
  const chosenKey = creditBlock ? familyBookingOfferingKey(creditBlock) : explicitStillExists
    ? explicitKey
    : familyBookingDefaultOfferingKey(offerings);
  // Remember the default as soon as it is shown. Refreshing a credit balance
  // must not choose a different lesson and discard its booking confirmation.
  // A student switch or removed offering can still choose a fresh default.
  useEffect(() => {
    setSelection((current) =>
      current.studentId === studentId && current.key === chosenKey
        ? current
        : { studentId, key: chosenKey },
    );
  }, [studentId, chosenKey]);
  const selectedOffering =
    offerings.find(
      (offering) => familyBookingOfferingKey(offering) === chosenKey,
    ) ?? null;
  const chosen = selectedOffering && creditBlock ? {
    ...selectedOffering,
    available_credits: Math.min(selectedOffering.available_credits, creditBlock.available_credits),
    lesson_block_ids: [creditBlock.lesson_block_id],
    request_onsite: { allowed: false, reasons: [] },
    book_with_credit: { ...selectedOffering.book_with_credit, allowed: selectedOffering.book_with_credit.allowed && creditBlock.booking.allowed },
  } : selectedOffering;
  const chosenPreference = chosen
    ? bookingPreferenceForOffering(preferences, chosen)
    : null;
  const usualShortcutPreference = chosen && scheduleFresh
    ? unambiguousUsualBookingPreference(offerings, schedule, chosen)
    : null;

  const handleBooked = () => {
    scheduleRead.reload();
    onBooked();
  };

  if (offerings.length === 0) {
    return (
      <div className="panel">
        <p className="empty">
          There is nothing available to book from the current credit view.{" "}
          {recentAmeliaPurchaseMessage}
        </p>
      </div>
    );
  }

  const creditOfferings = offerings.filter(
    (offering) => offering.book_with_credit.allowed && offering.available_credits > 0,
  );
  const otherOfferings = offerings.filter((offering) => !creditOfferings.includes(offering));
  const choicesOpen = !creditBlock && (choicesForStudent === studentId || chosen === null);

  function chooseOffering(offering: BookingOffering) {
    setSelection({ studentId, key: familyBookingOfferingKey(offering) });
    setChoicesForStudent(null);
  }

  function offeringChoices(choices: BookingOffering[]) {
    return (
      <div className="credit-booking-choice-grid">
        {choices.map((offering) => {
          const key = familyBookingOfferingKey(offering);
          return (
            <button
              key={key}
              className={key === chosenKey ? "" : "action-tertiary"}
              aria-pressed={key === chosenKey}
              onClick={() => chooseOffering(offering)}
              type="button"
            >
              <strong>{offering.service_name ?? "Lesson"}</strong>
              {offering.tutor_name && <span>{offering.tutor_name}</span>}
              <span className="small">
                {offering.book_with_credit.allowed && offering.available_credits > 0
                  ? `${offering.available_credits} lesson credit${offering.available_credits === 1 ? "" : "s"}`
                  : offering.request_onsite.allowed ? "Ask to pay on the day" : "Not currently available"}
              </span>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="panel credit-booking">
      <h2>Book a lesson</h2>
      {!creditUnconfirmed && <p className="credit-booking-lede">{creditBlock ? (chosen?.book_with_credit.allowed && chosen.available_credits > 0 ? "Choose a date and time. We'll use this credit block." : "Choose another lesson to continue booking.") : creditOfferings.length > 0 ? "Choose a date and time. We'll use your available credit." : "Choose a date and time to check booking options."}</p>}
      {chosen && chosen.book_with_credit.allowed && chosen.available_credits > 0 && (
        <div className="credit-booking-header">
          <div>
            <span className="credit-booking-eyebrow">{creditBlock ? "Selected credit block" : "Your available credit"}</span>
            <strong className="credit-booking-balance">
              {creditUnconfirmed
                ? "Balance being rechecked"
                : `${chosen.available_credits} lesson credit${chosen.available_credits === 1 ? "" : "s"}`}
            </strong>
            <div>{chosen.service_name ?? "Lesson"}{chosen.tutor_name && ` with ${chosen.tutor_name}`}</div>
            {creditBlock?.expires_at && <div className="small muted">Expires {formatDate(creditBlock.expires_at)}</div>}
            {chosenPreference && preferenceTiming(chosenPreference) && (
              <div className="small muted">{preferenceTiming(chosenPreference)}</div>
            )}
          </div>
          {!creditUnconfirmed && (creditBlock ? Boolean(onClearCredit) : offerings.length > 1) && (
            <button
              className="action-tertiary"
              type="button"
              aria-expanded={choicesOpen}
              onClick={() => creditBlock ? onClearCredit?.() : setChoicesForStudent(choicesOpen ? null : studentId)}
            >Change lesson</button>
          )}
        </div>
      )}
      {/* Without credit the header above is hidden, so a remembered
          pay-on-the-day lesson must still leave a way to pick another one. */}
      {chosen && !(chosen.book_with_credit.allowed && chosen.available_credits > 0)
        && !creditBlock && !creditUnconfirmed && offerings.length > 1 && (
        <div className="credit-booking-selected">
          <div>
            <span className="credit-booking-eyebrow">Selected lesson</span>
            <div>{chosen.service_name ?? "Lesson"}{chosen.tutor_name && ` with ${chosen.tutor_name}`}</div>
          </div>
          <button
            className="action-tertiary"
            type="button"
            aria-expanded={choicesOpen}
            onClick={() => setChoicesForStudent(choicesOpen ? null : studentId)}
          >Change lesson</button>
        </div>
      )}
      {choicesOpen && (
        <section className="credit-booking-choices" aria-label="Choose a lesson">
          <h3>{creditOfferings.length > 0 ? "Choose a lesson to use your credits" : "Choose a lesson"}</h3>
          {offeringChoices(creditOfferings)}
          {otherOfferings.length > 0 && (
            <details className="credit-booking-secondary" open={creditOfferings.length === 0}>
              <summary>Other lesson choices</summary>
              {offeringChoices(otherOfferings)}
            </details>
          )}
        </section>
      )}
      {chosen && (
        <Times
          key={`${studentId}:${chosenKey ?? ""}:${creditBlock?.lesson_block_id ?? ""}`}
          studentId={studentId}
          studentName={schedule?.student.fullName ?? null}
          offering={chosen}
          lessonBlockId={creditBlock?.lesson_block_id}
          schedule={schedule}
          usualPreference={usualShortcutPreference}
          confirmedHolds={confirmedHolds}
          setConfirmedHolds={setConfirmedHolds}
          onBooked={handleBooked}
          onWaitlistChanged={onWaitlistChanged}
        />
      )}
    </div>
  );
}

function failure(cause: unknown): string {
  if (bookingFailureKind(cause) === "uncertain") return uncertainBookingMessageFor(cause);
  if (cause instanceof ApiError) return cause.code ? reasonText(cause.code, cause.message) : cause.message;
  return "Something went wrong while booking.";
}

function Times({
  studentId,
  studentName,
  offering,
  lessonBlockId,
  schedule,
  usualPreference,
  confirmedHolds,
  setConfirmedHolds,
  onBooked,
  onWaitlistChanged,
}: {
  studentId: string;
  studentName: string | null;
  offering: BookingOffering;
  lessonBlockId?: string;
  schedule: StudentLessonSchedule | null;
  usualPreference: StudentBookingPreference | null;
  confirmedHolds: ConfirmedBookingHold[];
  setConfirmedHolds: Dispatch<SetStateAction<ConfirmedBookingHold[]>>;
  onBooked: () => void;
  onWaitlistChanged: () => void;
}) {
  const [slots, setSlots] = useState<AvailabilitySlot[] | null>(null);
  const [month, setMonth] = useState(() => `${isoDay(0).slice(0, 7)}-01`);
  const [dayChoice, setDayChoice] = useState<string | null>(null);
  const [multiple, setMultiple] = useState(false);
  const [payOnDay, setPayOnDay] = useState(
    !offering.book_with_credit.allowed || offering.available_credits <= 0,
  );
  const [availabilityLoading, setAvailabilityLoading] = useState(true);
  const [availabilityRevision, setAvailabilityRevision] = useState(0);
  const [noSlotsInNextFourWeeks, setNoSlotsInNextFourWeeks] = useState<
    boolean | null
  >(null);
  const [nextFourWeeksSlots, setNextFourWeeksSlots] = useState<AvailabilitySlot[] | null>(null);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  /**
   * Times chosen but not yet booked, in the order they were picked.
   *
   * A list rather than one slot, because a family holding a five-lesson block
   * came here to book five lessons. It used to be one at a time, and the picker
   * was replaced by the confirmation afterwards, so the only way to book a
   * second was to reload the page.
   */
  const [selected, setSelected] = useState<AvailabilitySlot[]>([]);
  const [method, setMethod] = useState<
    "cash" | "card_terminal" | "bank_transfer"
  >("cash");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const publish = usePublishActionOutcome();
  const [hosted, setHosted] = useState(false);
  function reportOutcome(result: ActionOutcome) { setOutcome(result); setHosted(publish(result)); }
  const [outcome, setOutcome] = useState<ActionOutcome | null>(null);
  const [resultSequence, setResultSequence] = useState(0);
  const [failures, setFailures] = useState<Record<string, BookingFailureView>>(
    {},
  );
  const [creditRejected, setCreditRejected] = useState(false);
  // One key per chosen time, kept until that time is booked or definitely
  // refused. This is what stops a double press spending two credits.
  const [keys, setKeys] = useState<Record<string, string>>({});
  // The single-slot panel, and the on-site path, are unchanged for one time.
  const chosen = selected.length === 1 ? selected[0]! : null;
  const [key, setKey] = useState(idempotencyKey);
  const creditEntitlementKey = `${offering.available_credits}:${offering.lesson_block_ids.join(",")}`;

  // A definite live-credit rejection is stronger evidence than a stale local
  // count. Keep that credit quarantined until the parent read actually changes
  // the entitlement identity/count (or this component is remounted).
  useEffect(() => {
    setCreditRejected(false);
  }, [studentId, creditEntitlementKey]);

  useEffect(() => {
    if (offering.available_credits <= 1) setMultiple(false);
  }, [offering.available_credits]);

  /**
   * An uncertain outcome blocks further choices only while its time is still
   * chosen.
   *
   * Reading every value in `failures` instead was a dead end with no way out.
   * Unpicking a time leaves its entry behind — deliberately, so a retry reuses
   * the same attempt key — and that invisible entry went on disabling every
   * slot button. "Start again", which is the only thing that clears them,
   * renders only while more than one time is chosen, so unpicking down to one
   * and then to none took the escape hatch away and left the whole picker
   * inert until the page was reloaded.
   *
   * Scoping it to the current selection keeps the guard where it does its work
   * — the uncertain time is on screen, tagged, and adding to it is refused —
   * while unpicking is always a way back. The protection that actually matters
   * is not this: it is `keys`, which survives unpicking and stops a retry
   * spending a second credit on the same time.
   */
  const hasUncertainFailure = selected.some(
    (slot) => failures[slot.starts_at]?.status === "uncertain",
  );
  const selectable = (slot: AvailabilitySlot) => ({
    startsAt: slot.starts_at,
    // The slot-level count is the precise eligibility answer. The offering
    // count is a fresh upper bound after a successful batch, so clamping to it
    // prevents an old selected slot from overstating what remains. A definite
    // live rejection temporarily overrides both until the parent entitlement
    // read actually changes.
    eligibleCredits: creditRejected
      ? 0
      : Math.min(slot.eligible_credit_count, offering.available_credits),
  });
  const isSelected = (slot: AvailabilitySlot) =>
    selected.some((current) => current.starts_at === slot.starts_at);
  const isHeldByConfirmedBooking = (slot: AvailabilitySlot) => confirmedHolds.some(hold =>
    hold.studentId === studentId && hold.providerId === slot.provider_id && hold.serviceId === slot.service_id &&
    hold.providerId === offering.provider_id && hold.serviceId === offering.service_id &&
    lessonIntervalsOverlap(hold, slot),
  );
  const creditSelectionUnavailable =
    selected.length > 0 &&
    (creditRejected || offering.available_credits <= 0 || !offering.book_with_credit.allowed) &&
    selected.every(bookableWithCredit);
  const capacityReached = hasUncertainFailure
    ? "A booking outcome is still uncertain. Check your lessons before choosing another time."
    : creditSelectionUnavailable
      ? "No lesson credits are currently available for this selection. Check your lessons and credits before trying again."
      : selectionLimitMessage(selected.map(selectable));
  function slotDisabled(slot: AvailabilitySlot): boolean {
    if (busy) return true;
    const picked = isSelected(slot);
    if (!picked && isHeldByConfirmedBooking(slot)) return true;
    if (!payOnDay && !bookableWithCredit(slot)) return true;
    if (!payOnDay && !picked && (creditRejected || offering.available_credits <= 0 || !offering.book_with_credit.allowed)) return true;
    // Beyond the credit cap the button is not merely inert: it is disabled, so
    // the limit is visible before it is hit rather than explained afterwards.
    // While an earlier attempt is uncertain, no new time is offered as if the
    // balance were known. Picked times stay enabled so they can always be
    // unpicked and the family never loses the recovery path.
    const beyondCredits =
      multiple && !payOnDay &&
      !picked &&
      bookableWithCredit(slot) &&
      selected.length > 0 &&
      selected.every(bookableWithCredit) &&
      !canAddSlot(selected.map(selectable), selectable(slot));
    return beyondCredits || (!picked && hasUncertainFailure);
  }

  function chooseSlot(slot: AvailabilitySlot) {
    setError(null);
    if (!isSelected(slot) && isHeldByConfirmedBooking(slot)) {
      setError("That time overlaps a recently confirmed lesson and is no longer available.");
      return;
    }
    if (isSelected(slot)) {
      setSelected((current) =>
        current.filter((one) => one.starts_at !== slot.starts_at),
      );
      return;
    }
    if (!multiple && !hasUncertainFailure) {
      setKey(idempotencyKey());
      setKeys((current) => ({
        ...current,
        [slot.starts_at]: current[slot.starts_at] ?? idempotencyKey(),
      }));
      setSelected([slot]);
      return;
    }
    // Until an uncertain request is reconciled, adding another lesson would
    // pretend we know whether its credit was spent. The existing uncertain
    // selection can still be retried with the same attempt key, or explicitly
    // cleared and revisited after checking the lessons list.
    if (hasUncertainFailure) return;
    // A credit-funded time is capped by the credits that cover it. An on-site
    // request spends nothing, so it is never part of a multiple selection —
    // asking a tutor to hold five times at once is a different thing to do to
    // them, and is deliberately not offered here. After a definite live-credit
    // rejection we still allow a single selection when on-site payment is an
    // option, but do not let the stale credit participate in capacity.
    if (
      bookableWithCredit(slot) &&
      selected.every(bookableWithCredit) &&
      !creditRejected
    ) {
      if (!canAddSlot(selected.map(selectable), selectable(slot))) return;
      setKeys((current) => ({
        ...current,
        [slot.starts_at]: current[slot.starts_at] ?? idempotencyKey(),
      }));
      setSelected((current) => [...current, slot]);
      return;
    }
    if (creditRejected && !offering.request_onsite.allowed) return;
    setKey(idempotencyKey());
    setSelected([slot]);
  }

  function clearSelection() {
    setSelected([]);
    setFailures({});
  }

  function choosePaymentMethod(nextMethod: typeof method) {
    if (nextMethod !== method) setKey(idempotencyKey());
    setMethod(nextMethod);
    setError(null);
  }

  function handleBookingFailure(cause: unknown) {
    setError(failure(cause));
    const attempt = bookingAttemptAfterFailure(key, cause, idempotencyKey);
    setKey(attempt.key);
    if (attempt.kind === "rejected") {
      setSlots(null);
      clearSelection();
    }
  }

  const today = isoDay(0);
  const window = availabilityMonthWindow(month, today);

  // Fetch one supported month window, then group its authoritative slots by
  // London date. Calendar highlighting never invents a bookable date.
  useEffect(() => {
    let cancelled = false;
    setAvailabilityLoading(true);
    setSlots(null);
    setError(null);

    void booking
      .availability(studentId, {
        provider_id: String(offering.provider_id),
        service_id: String(offering.service_id),
        start_date: window.startDate,
        end_date: window.endDate,
        ...(!payOnDay && lessonBlockId ? { lesson_block_id: lessonBlockId } : {}),
      })
      .then((availability) => {
        if (!cancelled) {
          setSlots(availability.slots);
          setSelected((current) =>
            reconcileSelectedAvailability(current, availability.slots),
          );
        }
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(failure(cause));
      })
      .finally(() => {
        if (!cancelled) setAvailabilityLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    studentId,
    offering.provider_id,
    offering.service_id,
    window.startDate,
    window.endDate,
    availabilityRevision,
    creditEntitlementKey,
    lessonBlockId,
    payOnDay,
  ]);

  // An empty picker range is not evidence that this offering has no meaningful
  // availability. Keep this independent, fixed four-week check so the automatic
  // waitlist prompt still appears only when there really is nothing to choose.
  // Families can now also open the same form deliberately when available times
  // exist but none of them are workable.
  useEffect(() => {
    let cancelled = false;
    const checkStart = isoDay(0);
    setNoSlotsInNextFourWeeks(null);
    setNextFourWeeksSlots(null);
    void booking
      .availability(studentId, {
        provider_id: String(offering.provider_id),
        service_id: String(offering.service_id),
        start_date: checkStart,
        end_date: fourWeeksFrom(checkStart),
        ...(!payOnDay && lessonBlockId ? { lesson_block_id: lessonBlockId } : {}),
      })
      .then((availability) => {
        if (!cancelled) {
          setNextFourWeeksSlots(availability.slots);
          setNoSlotsInNextFourWeeks(!availability.slots.some((slot) =>
            offering.request_onsite.allowed ||
            (offering.book_with_credit.allowed && offering.available_credits > 0 && !creditRejected && bookableWithCredit(slot)),
          ));
        }
      })
      .catch(() => {
        // A failed check is not a reason to claim there is no availability.
        if (!cancelled) {
          setNextFourWeeksSlots(null);
          setNoSlotsInNextFourWeeks(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [studentId, offering.provider_id, offering.service_id, offering.request_onsite.allowed, offering.book_with_credit.allowed, creditRejected, creditEntitlementKey, lessonBlockId, payOnDay]);

  function changeMonth(nextMonth: string) {
    setMonth(nextMonth);
    setDayChoice(null);
    clearSelection();
    setSlots(null);
  }

  /**
   * Book every chosen time.
   *
   * The picker stays on screen afterwards, with the confirmation above it and
   * the booked times gone from the grid. Replacing it with a notice is what
   * made booking a second lesson mean reloading the page.
   */
  async function bookSelected() {
    if (selected.length === 0) return;
    if (selected.some(isHeldByConfirmedBooking)) {
      setError("A selected time overlaps a recently confirmed lesson. Choose another time before booking.");
      setSelected(current => current.filter(slot => !isHeldByConfirmedBooking(slot)));
      return;
    }
    if (creditSelectionUnavailable) {
      setError(
        "No lesson credits are currently available for this selection. Check your lessons and credits before trying again.",
      );
      return;
    }
    setBusy(true);
    setError(null);
    setFailures({});

    const accepted = new Map<string, MutationResult>();
    let creditStateChanged = false;
    const byStart = new Map(selected.map((slot) => [slot.starts_at, slot]));
    const report = await bookEachSlot(
      selected.map((slot) => ({ startsAt: slot.starts_at })),
      keys,
      async (startsAt, attemptKey) => {
        const slot = byStart.get(startsAt);
        if (!slot) return;
        try {
          const result = await booking.bookWithCredit(
            studentId,
            {
              provider_id: slot.provider_id,
              service_id: slot.service_id,
              starts_at: slot.starts_at,
              ...(lessonBlockId ? { lesson_block_id: lessonBlockId } : {}),
            },
            attemptKey,
          );
          if (!result || result.booking_status !== "approved" || !Number.isFinite(result.appointment_id) || !Number.isFinite(result.booking_id) || result.appointment_id <= 0 || result.booking_id <= 0 || !result.credit || !result.payment || !["refreshed", "queued", "pending_retry"].includes(result.mirror_sync?.state)) {
            throw new Error("The booking service did not confirm an approved lesson. Check lessons and credits before retrying this same attempt.");
          }
          accepted.set(startsAt, result);
        } catch (cause) {
          if (
            cause instanceof ApiError &&
            cause.code === "credit_coupon_unavailable"
          ) {
            creditStateChanged = true;
          }
          throw cause;
        }
      },
      idempotencyKey,
      failure,
    );

    const booked = new Set(report.booked);
    setConfirmedHolds(current => {
      const byBooking = new Map(current.map(hold => [`${hold.studentId}:${hold.providerId}:${hold.serviceId}:${hold.requestId ?? hold.bookingId}`, hold]));
      for (const startsAt of report.booked) {
        const slot = byStart.get(startsAt);
        const reply = accepted.get(startsAt);
        if (!slot || !reply) continue;
        const hold = {
          studentId,
          providerId: slot.provider_id,
          serviceId: slot.service_id,
          bookingId: reply.booking_id,
          appointmentId: reply.appointment_id,
          starts_at: slot.starts_at,
          ends_at: slot.ends_at,
          presentInSchedule: false,
        };
        byBooking.set(`${studentId}:${slot.provider_id}:${slot.service_id}:${reply.booking_id}`, hold);
      }
      return [...byBooking.values()];
    });
    setSelected((current) =>
      current.filter((slot) => !booked.has(slot.starts_at)),
    );
    setSlots(
      (current) =>
        current?.filter((slot) => !booked.has(slot.starts_at)) ?? current,
    );
    setKeys(report.keys);
    setFailures(
      Object.fromEntries(
        report.outcomes
          .filter((outcome) => outcome.status !== "booked")
          .map((outcome) => [
            outcome.startsAt,
            {
              status:
                outcome.status === "uncertain"
                  ? ("uncertain" as const)
                  : ("rejected" as const),
              message: outcome.message ?? "That could not be booked.",
            },
          ]),
      ),
    );
    setDone(bulkBookingSummary(report));
    setResultSequence((value) => value + 1);
    const unknown = report.outcomes.some((item) => item.status === "uncertain");
    const incomplete = report.outcomes.some((item) => item.status !== "booked");
    const pending = [...accepted.values()].some((reply) => reply.credit_sync === "pending_reconciliation" || reply.mirror_sync.state !== "refreshed");
    const replayed = report.booked.length === 1 && accepted.get(report.booked[0]!)?.replayed;
    reportOutcome({
      variant: unknown ? "uncertain" : incomplete || pending ? "attention" : "success",
      title: unknown ? "Booking outcomes need checking" : replayed ? "Earlier lesson booking confirmed" : report.booked.length === 1 ? "Lesson booked" : report.booked.length > 1 ? `${report.booked.length} lessons booked` : "Lessons not booked",
      description: studentName,
      details: [
        ...report.booked.flatMap((startsAt) => {
          const reply = accepted.get(startsAt)!;
          return [{ value: `${formatDate(startsAt)} · ${formatTime(startsAt)}` },
            { value: describeCredit(reply.credit, "confirmed") }, { value: describePayment(reply.payment) },
            ...(reply.replayed ? [{ value: "The earlier booking was confirmed; no second lesson was booked." }] : []),
            ...(reply.credit_sync === "pending_reconciliation" ? [{ value: "The lesson is booked, but credit reconciliation is still pending. The account balance is not final; check credits before another booking." }] : []),
            ...(reply.mirror_sync.state !== "refreshed" ? [{ value: "The lesson is booked, but the diary update is still pending. Do not book this lesson again." }] : [])];
        }),
        ...(offering.service_name ? [{ value: offering.service_name }] : []),
        { value: bulkBookingSummary(report) },
      ],
    });
    setBusy(false);
    // Refresh after every batch, not only a confirmed success. An uncertain
    // response may have committed server-side, and a definite refusal may have
    // made a previously-visible slot unavailable for another reason.
    setAvailabilityRevision((current) => current + 1);
    if (creditStateChanged) setCreditRejected(true);
    // Ask the parent to reload offerings so any reconciliation that has already
    // caught up is reflected immediately. The local quarantine above remains
    // authoritative if that fresh read still carries the same stale balance.
    if (report.booked.length > 0 || creditStateChanged) onBooked();
  }

  async function requestOnSite() {
    if (!chosen) return;
    if (isHeldByConfirmedBooking(chosen)) {
      setError("That time overlaps a recently confirmed lesson. Choose another time.");
      setSelected([]);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const reply = await booking.requestOnSite(
        studentId,
        {
          provider_id: chosen.provider_id,
          service_id: chosen.service_id,
          starts_at: chosen.starts_at,
          payment_method: method,
        },
        key,
      );
      // The time is held until the tutor answers, so it must not be offered
      // again from availability that has not caught up yet.
      setConfirmedHolds(current => [...current, {
        studentId,
        providerId: chosen.provider_id,
        serviceId: chosen.service_id,
        bookingId: 0,
        appointmentId: 0,
        requestId: reply?.id ?? `${chosen.starts_at}:${chosen.provider_id}:${chosen.service_id}`,
        starts_at: chosen.starts_at,
        ends_at: chosen.ends_at,
        presentInSchedule: false,
      }]);
      setDone(
        `Asked for ${formatDate(chosen.starts_at)} at ${formatTime(chosen.starts_at)}. Awaiting tutor confirmation: the time is held until your tutor confirms it, and you will see it on your lessons once they have.`,
      );
      setResultSequence((value) => value + 1);
      reportOutcome({ variant: "attention", title: "Lesson requested", description: studentName,
        details: [{ value: `${formatDate(chosen.starts_at)} · ${formatTime(chosen.starts_at)}` }, { value: "Awaiting tutor confirmation. The time is held until your tutor confirms it. Check your lessons for confirmation." }] });
      setSelected([]);
      setKey(idempotencyKey());
      onBooked();
    } catch (cause) {
      handleBookingFailure(cause);
    } finally {
      setBusy(false);
    }
  }

  const bookingStatus = done && !hosted && (
    <ActionConfirmation key={resultSequence} {...(outcome ?? { variant: "attention", title: "Lesson requested", description: done })} reveal />
  );

  // Neither way of paying is open, but spending the last credit must still
  // leave the result of the booking visible.
  if (!offering.book_with_credit.allowed && !offering.request_onsite.allowed) {
    return (
      <div className="booking-times-feedback">
        {bookingStatus}
        <Reasons decision={offering.book_with_credit} />
        <Reasons decision={offering.request_onsite} />
      </div>
    );
  }

  const duplicateWarnings = duplicateWarningsForSelection(schedule, selected);
  const availabilitySettled = !availabilityLoading && slots !== null;
  const usableSlots = (slots ?? []).filter(
    (slot) => dayKey(slot.starts_at) >= today && dayKey(slot.starts_at).slice(0, 7) === month.slice(0, 7)
      && !isHeldByConfirmedBooking(slot)
      && (payOnDay ? offering.request_onsite.allowed : bookableWithCredit(slot)),
  );
  const shortcutSlots = (nextFourWeeksSlots ?? []).filter((slot) =>
    slot.provider_id === offering.provider_id &&
    slot.service_id === offering.service_id &&
    !isHeldByConfirmedBooking(slot) &&
    (payOnDay
      ? offering.request_onsite.allowed
      : offering.book_with_credit.allowed &&
        offering.available_credits > 0 &&
        !creditRejected &&
        bookableWithCredit(slot)),
  );
  const usualAlternatives = usualPreference && nextFourWeeksSlots !== null &&
      selected.length === 0 && !multiple
    ? nearestUsualAvailabilitySlots(
        shortcutSlots,
        usualPreference,
        new Date(),
        3,
        latestConfirmedUpcomingLessonStart(schedule, usualPreference),
      )
    : [];
  const usualTimeAvailableSlot = usualPreference
    ? usualAlternatives.find((slot) => slotMatchesUsualPreference(slot, usualPreference)) ?? null
    : null;
  const usualTimeAvailable = usualTimeAvailableSlot !== null;
  const days = groupAvailabilitySlots(usableSlots);
  const availableDays = new Map(days.map(([day, times]) => [day, times.length]));
  const selectedDay = dayChoice !== null && availableDays.has(dayChoice) ? dayChoice : days[0]?.[0] ?? null;
  const daySlots = days.find(([day]) => day === selectedDay)?.[1] ?? [];

  return (
    <div>
      {bookingStatus}
      {error && <div className="error" role="alert">{error}</div>}
      <div className="credit-booking-layout">
        <div>
          {usualPreference && availabilitySettled && usualAlternatives.length > 0 && (
            <section className="notice" aria-label="Book my usual lesson">
              <strong>{usualTimeAvailableSlot
                ? `Your usual time is available on ${formatDate(usualTimeAvailableSlot.starts_at)}`
                : "Your usual time is unavailable"}</strong>
              <p className="small">
                {usualTimeAvailableSlot
                  ? "Choose this provider-confirmed time. You will review the lesson and credit before booking."
                  : "These nearby alternatives are confirmed for your current tutor, lesson and available credit or payment option."}
              </p>
              <div className="row">
                {usualAlternatives.map((slot) => (
                  <button
                    key={slot.starts_at}
                    className="action-tertiary"
                    type="button"
                    disabled={slotDisabled(slot)}
                    onClick={() => {
                      const day = dayKey(slot.starts_at);
                      setMonth(day.slice(0, 7) + "-01");
                      setDayChoice(day);
                      chooseSlot(slot);
                    }}
                  >
                    {(slotMatchesUsualPreference(slot, usualPreference)
                      ? "Usual time"
                      : "Nearby time") + " · " + formatDate(slot.starts_at) + " at " + formatTime(slot.starts_at)}
                  </button>
                ))}
              </div>
            </section>
          )}
          <AvailabilityCalendar
            month={month}
            today={today}
            availableDays={availableDays}
            selectedDay={selectedDay}
            loading={availabilityLoading}
            busy={busy}
            onMonthChange={changeMonth}
            onDayChange={setDayChoice}
          />
          {availabilityLoading && <p className="small muted" role="status">Looking for free times…</p>}
          {offering.tutor_id && <RequestTimeEntry key={`${studentId}:${offering.tutor_id}:${offering.service_id}`} studentId={studentId} tutorId={offering.tutor_id} serviceId={offering.service_id} providerId={offering.provider_id} serviceName={offering.service_name ?? "Lesson"} initialDate={selectedDay ?? isoDay(0)} onChanged={onWaitlistChanged} onFree={chooseSlot} />}
          {availabilitySettled && days.length === 0 && (
            <p className="empty">No {payOnDay ? "free" : "credit-bookable"} times in this month. Try another month.</p>
          )}
          {availabilitySettled && noSlotsInNextFourWeeks === false && (
            <div className="credit-booking-waitlist">
              {!waitlistOpen ? (
                <button className="action-tertiary" type="button" onClick={() => setWaitlistOpen(true)}>
                  None of these times work — join the waiting list
                </button>
              ) : (
                <>
                  <button className="action-tertiary small" type="button" onClick={() => setWaitlistOpen(false)}>Hide waiting list options</button>
                  <WaitlistForm studentId={studentId} offering={offering} onSubmitted={onWaitlistChanged} reason="times-dont-work" />
                </>
              )}
            </div>
          )}
          {availabilitySettled && noSlotsInNextFourWeeks && (
            <WaitlistForm studentId={studentId} offering={offering} onSubmitted={onWaitlistChanged} reason="no-availability" />
          )}
        </div>
        <section className="credit-booking-times" aria-label="Choose a time">
          {selectedDay ? (
            <div className="availability-day">
              <h3 className="day-heading">{formatWeekdayDay(selectedDay)}</h3>
              <p className="small muted">Choose a time · UK time</p>
              <div className="slot-grid">
                {daySlots.map((slot) => (
                  <button
                    key={slot.starts_at}
                    type="button"
                    aria-pressed={isSelected(slot)}
                    className={isSelected(slot) ? "action-primary" : "action-tertiary"}
                    disabled={slotDisabled(slot)}
                    onClick={() => chooseSlot(slot)}
                  >{formatTime(slot.starts_at)}</button>
                ))}
              </div>
            </div>
          ) : <p className="small muted">Choose an available day to see lesson times.</p>}
          {!payOnDay && offering.available_credits > 1 && (
            <button
              className="action-tertiary small"
              type="button"
              aria-pressed={multiple}
              disabled={busy || hasUncertainFailure}
              onClick={() => { clearSelection(); setMultiple(!multiple); }}
            >{multiple ? "Book one lesson" : "Book several lessons"}</button>
          )}
          {multiple && <p className="small muted">Choose times from any highlighted day in this month. Each lesson uses one credit.</p>}
          {duplicateWarnings.map(({ slot, lesson }) => (
            <div className="notice" role="note" key={`${slot.starts_at}:${lesson.bookingId}`}>
              <strong>{studentName ?? "This student"} already has this kind of lesson that week</strong>
              <div className="small">{formatDate(lesson.startsAt)} at {formatTime(lesson.startsAt)}{lesson.tutorName && ` with ${lesson.tutorName}`}. You can still continue if you mean to book an extra lesson.</div>
            </div>
          ))}
          {selected.length > 0 && (
            <div className="booking-checkout">
              <div className="credit-booking-summary">
                <strong>Your {selected.length === 1 ? "booking" : "bookings"}</strong>
                <div>{offering.service_name ?? "Lesson"}{offering.tutor_name && ` · ${offering.tutor_name}`}</div>
                <div className="small">{studentName ?? "Selected learner"}{chosen && ` · ${Math.round((Date.parse(chosen.ends_at) - Date.parse(chosen.starts_at)) / 60000)} minutes`}{payOnDay ? " · Pay on the day" : " · Lesson credits"}</div>
                {chosen ? <div>{formatDate(chosen.starts_at)} · {formatTime(chosen.starts_at)}–{formatTime(chosen.ends_at)}</div> : (
                  <ul className="small credit-booking-selected-list">
                    {selected.map((slot) => <li key={slot.starts_at}>{formatDate(slot.starts_at)} at {formatTime(slot.starts_at)}{failures[slot.starts_at] && <strong className="tag warn">{failures[slot.starts_at]!.status === "uncertain" ? "Not confirmed" : "Not booked"}</strong>}</li>)}
                  </ul>
                )}
              </div>
              <FailureDetail failures={failures} selected={selected} />
              {chosen && !payOnDay && !bookableWithCredit(chosen) && <Reasons decision={chosen.booking} />}
              {!payOnDay && capacityReached && <p className="small muted">{capacityReached}</p>}
              {!payOnDay && selected.every(bookableWithCredit) && (
                <div className="credit-booking-confirm">
                  <strong>{chosen ? `${formatDate(chosen.starts_at)} · ${formatTime(chosen.starts_at)}` : `${selected.length} lessons selected`}{offering.tutor_name && ` · ${offering.tutor_name}`}</strong>
                  <p className="small muted">
                    {creditSelectionUnavailable ? "No lesson credits are currently available for this selection." : `Uses ${selected.length} credit${selected.length === 1 ? "" : "s"} · ${Math.max(0, offering.available_credits - selected.length)} remaining`}
                  </p>
                  <button disabled={busy || creditSelectionUnavailable} type="button" onClick={() => void bookSelected()}>
                    {busy ? "Booking…" : selected.length === 1 ? hasUncertainFailure ? "Retry confirmation" : "Confirm booking" : `Book ${selected.length} lessons`}
                  </button>
                </div>
              )}
              {payOnDay && chosen && offering.request_onsite.allowed && (
                <div>
                  <p className="small"><strong>Ask to pay on the day.</strong> This holds the time and asks your tutor to confirm it. It is not booked until they do, and nothing is paid now.</p>
                  <label className="stack"><span className="small muted">How you would pay</span>
                    <select value={method} onChange={(event) => choosePaymentMethod(event.target.value as typeof method)}>
                      <option value="cash">Cash</option><option value="card_terminal">Card machine</option><option value="bank_transfer">Bank transfer</option>
                    </select>
                  </label>
                  <button className="action-secondary" type="button" disabled={busy} onClick={() => void requestOnSite()}>{busy ? "Asking…" : "Ask for this time"}</button>
                </div>
              )}
              <button className="action-tertiary small" type="button" disabled={busy} onClick={clearSelection}>Clear selection</button>
            </div>
          )}
          {!lessonBlockId && offering.request_onsite.allowed && (!payOnDay || (offering.book_with_credit.allowed && offering.available_credits > 0)) && (
            <details className="credit-booking-secondary">
              <summary>Other ways to book</summary>
              <button className="action-tertiary" type="button" disabled={busy || hasUncertainFailure} onClick={() => { clearSelection(); setMultiple(false); setPayOnDay(!payOnDay); }}>
                {payOnDay ? "Use lesson credits instead" : "Ask to pay on the day instead"}
              </button>
            </details>
          )}
        </section>
      </div>
    </div>
  );
}

/**
 * Which of the chosen times did not get booked, and why.
 *
 * A refusal and an unknown outcome are deliberately rendered in separate
 * blocks. "Not booked" / "no credit was used" are claims supported by a
 * definite refusal only; a timeout or 5xx may have committed and must never sit
 * under those words merely because another row in the same batch was rejected.
 */
function FailureDetail({
  failures,
  selected,
}: {
  failures: Record<string, BookingFailureView>;
  selected: AvailabilitySlot[];
}) {
  const rows = selected
    .map((slot) => ({ slot, failure: failures[slot.starts_at] }))
    .filter(
      (row): row is { slot: AvailabilitySlot; failure: BookingFailureView } =>
        row.failure !== undefined,
    );
  if (rows.length === 0) return null;

  const { rejected, uncertain } = partitionBookingFailureRows(rows);

  return (
    <>
      {rejected.length > 0 && (
        <div className="error" style={{ marginTop: 10 }} role="alert">
          <p style={{ marginTop: 0 }}>
            <strong>
              {rejected.length === 1
                ? "This time was not booked"
                : "These times were not booked"}
            </strong>
          </p>
          <ul className="small" style={{ margin: "6px 0 0", paddingLeft: 18 }}>
            {rejected.map(({ slot, failure }) => (
              <li key={slot.starts_at}>
                <strong>
                  {formatDate(slot.starts_at)} at {formatTime(slot.starts_at)}
                </strong>{" "}
                — {failure.message}
              </li>
            ))}
          </ul>
          <p className="small" style={{ marginBottom: 0 }}>
            No credit was used for {rejected.length === 1 ? "it" : "these"}.
            Pick another time, or try again.
          </p>
        </div>
      )}

      {uncertain.length > 0 && (
        <div className="error" style={{ marginTop: 10 }} role="alert">
          <p style={{ marginTop: 0 }}>
            <strong>
              {uncertain.length === 1
                ? "This time could not be confirmed"
                : "These times could not be confirmed"}
            </strong>
          </p>
          <ul className="small" style={{ margin: "6px 0 0", paddingLeft: 18 }}>
            {uncertain.map(({ slot, failure }) => (
              <li key={slot.starts_at}>
                <strong>
                  {formatDate(slot.starts_at)} at {formatTime(slot.starts_at)}
                </strong>{" "}
                — {failure.message}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

const WEEKDAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;

interface EditablePattern {
  id: number;
  weekday: number;
  starts: string;
  ends: string;
}

function minutesOf(value: string): number | null {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}

function nextFreePattern(patterns: readonly EditablePattern[]): { weekday: number; starts: string; ends: string } | null {
  const windows = [[9, 10], [10, 11], [13, 14], [14, 15], [16, 17], [17, 18]] as const;
  for (let weekday = 0; weekday < 7; weekday += 1) {
    for (const [startHour, endHour] of windows) {
      const startsMinutes = startHour * 60;
      const endsMinutes = endHour * 60;
      const overlaps = patterns.some((pattern) => {
        if (pattern.weekday !== weekday) return false;
        const starts = minutesOf(pattern.starts);
        const ends = minutesOf(pattern.ends);
        return starts !== null && ends !== null && startsMinutes < ends && endsMinutes > starts;
      });
      if (!overlaps) {
        return {
          weekday,
          starts: `${String(startHour).padStart(2, "0")}:00`,
          ends: `${String(endHour).padStart(2, "0")}:00`,
        };
      }
    }
  }
  return null;
}

type WaitlistReason = "no-availability" | "times-dont-work";

/** Shared form for the automatic empty-calendar path and a family's deliberate choice. */
function WaitlistForm({
  studentId,
  offering,
  onSubmitted,
  reason,
}: {
  studentId: string;
  offering: BookingOffering;
  onSubmitted: () => void;
  reason: WaitlistReason;
}) {
  const [patterns, setPatterns] = useState<EditablePattern[]>([
    { id: 1, weekday: 0, starts: "16:00", ends: "18:00" },
  ]);
  const [shortNoticeOptIn, setShortNoticeOptIn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  if (submitted) {
    return <ActionConfirmation variant="success" title="Waiting list request submitted" description={offering.tutor_name ?? "Your tutor"}
      details={[{ value: "You can track it under Your waiting list requests below." }]} reveal />;
  }

  if (!offering.tutor_id) {
    return (
      <div className="notice" style={{ marginTop: 12 }}>
        <strong>
          {reason === "no-availability"
            ? "No free times in the next four weeks."
            : "Waiting list unavailable for this tutor."}
        </strong>
        <div className="small muted">
          This tutor is not linked to the Hub waiting list yet. Please contact the
          school to ask about another time.
        </div>
      </div>
    );
  }
  const tutorId = offering.tutor_id;

  const edit = (id: number, change: Partial<Omit<EditablePattern, "id">>) => {
    setPatterns((current) =>
      current.map((pattern) =>
        pattern.id === id ? { ...pattern, ...change } : pattern,
      ),
    );
    setError(null);
  };

  const add = () => {
    setPatterns((current) => {
      const suggested = nextFreePattern(current);
      if (!suggested) return current;
      return [
        ...current,
        {
          id: Math.max(...current.map((pattern) => pattern.id), 0) + 1,
          ...suggested,
        },
      ];
    });
  };

  async function submit() {
    const requested = patterns.map((pattern) => ({
      weekday: pattern.weekday,
      startsMinutes: minutesOf(pattern.starts),
      endsMinutes: minutesOf(pattern.ends),
    }));
    const complete = requested.every(
      (
        pattern,
      ): pattern is {
        weekday: number;
        startsMinutes: number;
        endsMinutes: number;
      } => pattern.startsMinutes !== null && pattern.endsMinutes !== null,
    );
    if (!complete) {
      setError("Choose a start and finish time for every day.");
      return;
    }

    setBusy(true);
    setError(null);
    try {
      // Working hours and time-off validation belong to this endpoint. The
      // browser sends the requested pattern and renders its 422 message; it
      // deliberately does not maintain a second copy of those rules.
      await waitlistApi.createRequest({
        studentId,
        tutorId,
        serviceId: offering.service_id,
        serviceName: offering.service_name ?? "Lesson",
        patterns: requested,
        shortNoticeOptIn,
      });
      setSubmitted(true);
      onSubmitted();
    } catch (cause) {
      setError(
        cause instanceof ApiError
          ? cause.message
          : "That request could not be sent.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="notice" style={{ marginTop: 12 }}>
      <p style={{ marginTop: 0 }}>
        <strong>
          {reason === "no-availability"
            ? "No free times in the next four weeks."
            : "Join the waiting list for a better time"}
        </strong>
      </p>
      <p className="small">
        {reason === "times-dont-work" &&
          "If the available times do not suit you, you can wait for something that does. "}
        Set a recurring preference for {offering.service_name ?? "this lesson"} with{" "}
        {offering.tutor_name ?? "this tutor"}. Choose every day and time window
        that could work for you. A released time is offered only when your tutor
        has approved the request.
      </p>
      <p className="small muted">
        For a one-off date and time, use the specific-time request in the calendar above.
      </p>
      {error && <div className="error small">{error}</div>}
      <div className="stack">
        {patterns.map((pattern) => (
          <div className="row" key={pattern.id}>
            <label className="stack">
              <span className="small muted">Day</span>
              <select
                aria-label="Waiting list day"
                value={pattern.weekday}
                onChange={(event) =>
                  edit(pattern.id, { weekday: Number(event.target.value) })
                }
              >
                {WEEKDAYS.map((day, weekday) => (
                  <option key={day} value={weekday}>
                    {day}
                  </option>
                ))}
              </select>
            </label>
            <label className="stack">
              <span className="small muted">From</span>
              <input
                aria-label="Waiting list start time"
                onChange={(event) =>
                  edit(pattern.id, { starts: event.target.value })
                }
                step={900}
                type="time"
                value={pattern.starts}
              />
            </label>
            <label className="stack">
              <span className="small muted">To</span>
              <input
                aria-label="Waiting list finish time"
                onChange={(event) =>
                  edit(pattern.id, { ends: event.target.value })
                }
                step={900}
                type="time"
                value={pattern.ends}
              />
            </label>
            {patterns.length > 1 && (
              <button
                aria-label="Remove this waiting list time"
                className="action-tertiary small"
                disabled={busy}
                onClick={() =>
                  setPatterns((current) =>
                    current.filter((one) => one.id !== pattern.id),
                  )
                }
                type="button"
              >
                Remove
              </button>
            )}
          </div>
        ))}
      </div>
      <label className="small waitlist-short-notice">
        <input
          type="checkbox"
          checked={shortNoticeOptIn}
          disabled={busy}
          onChange={(event) => setShortNoticeOptIn(event.target.checked)}
        />
        I’m interested in lessons with less than 24 hours’ notice.
      </label>
      <p className="small muted">
        This records interest only. It does not approve a booking; the tutor must approve the exact time.
      </p>
      <div className="row" style={{ marginTop: 12 }}>
        <button
          className="action-tertiary small"
          disabled={busy || patterns.length >= 21 || nextFreePattern(patterns) === null}
          onClick={add}
          type="button"
        >
          Add another day or time
        </button>
        <button disabled={busy} onClick={() => void submit()} type="button">
          {busy ? "Sending…" : "Join the waiting list"}
        </button>
      </div>
    </div>
  );
}
